import { useCallback, useEffect, useState } from 'react';
import { Modal } from '@telegram-apps/telegram-ui';
import { get } from '../../helpers/api';
import { firstChineseChar } from '../../helpers/firstChineseChar';
import { BarChart, type ChartPoint } from '../BarChart/BarChart';
import { ProxyRecordsModal } from '../ProxyRecordsModal/ProxyRecordsModal';
import styles from './ProxyChartModal.module.css';

const PERIOD_LABEL: Record<number, string> = {
  1: '今天',
  2: '昨天',
  3: '近7天',
  4: '近30天',
  5: '上月',
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proxyId: number;
  proxyName: string;
  period?: number;
};

export const ProxyChartModal = ({ open, onOpenChange, proxyId, proxyName, period = 4 }: Props) => {
  const [points, setPoints] = useState<ChartPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logsOpen, setLogsOpen] = useState(false);
  const [recordsKind, setRecordsKind] = useState<'activate' | 'recycle'>('activate');

  const openRecords = (kind: 'activate' | 'recycle') => {
    setRecordsKind(kind);
    setLogsOpen(true);
  };

  const isHourly = period === 1 || period === 2;
  const periodLabel = PERIOD_LABEL[period] ?? '近30天';

  const fetchChart = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await get<ChartPoint[]>(
        `/admin/tel/proxy/chart?proxyId=${proxyId}&period=${period}`
      );
      setPoints(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [proxyId, period]);

  useEffect(() => {
    if (open) {
      fetchChart();
    }
  }, [open, fetchChart]);

  const totalActivate = points.reduce((sum, p) => sum + (Number(p.activateCount) || 0), 0);
  const totalRecycle = points.reduce((sum, p) => sum + (Number(p.recycleCount) || 0), 0);
  const peak = points.reduce(
    (best, p) => {
      const total = (Number(p.activateCount) || 0) + (Number(p.recycleCount) || 0);
      return total > best.total ? { total, date: p.date, activate: Number(p.activateCount) || 0 } : best;
    },
    { total: 0, date: '', activate: 0 }
  );
  const dayCount = isHourly ? 1 : points.length;
  const avgDaily = dayCount > 0 ? totalActivate / dayCount : 0;
  const fmtAvg = avgDaily.toLocaleString('zh-CN', { maximumFractionDigits: 1 });
  const peakLabel = isHourly ? peak.date.slice(11, 16) : peak.date.slice(5);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      className="app-modal"
      header={
        <div className={styles.banner}>
          <div className={styles.bannerTitle}>
            <span className={styles.bannerTitleAvatar}>
              {firstChineseChar(proxyName || '')}
            </span>
            <span className={styles.bannerTitleName}>{proxyName}</span>
            <span className={styles.bannerTitleSuffix}>激活趋势</span>
          </div>
          <div className={styles.bannerSub}>
            {periodLabel}{isHourly ? '按小时' : '按天'}激活与回收情况，蓝色为激活，紫色为回收。
          </div>
          <div className={styles.bannerBars} aria-hidden="true">
            <i /><i /><i />
          </div>
        </div>
      }
    >
      <div className={styles.body}>
        {loading ? (
          <div className={styles.loading}>
            <span className="spinner" />
          </div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : points.length > 0 ? (
          <>
            <div className={styles.summary}>
              <button
                type="button"
                className={`${styles.stat} ${styles.statClickable}`}
                onClick={() => openRecords('activate')}
              >
                <div className="stat-icon blue">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M13 2 4 14h7l-1 8 9-12h-7z" />
                  </svg>
                </div>
                <div className={styles.statLabel}>累计激活</div>
                <div className={styles.statValue}>
                  {totalActivate.toLocaleString()}
                </div>
              </button>
              <button
                type="button"
                className={`${styles.stat} ${styles.statClickable}`}
                onClick={() => openRecords('recycle')}
              >
                <div className="stat-icon green">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 12a9 9 0 0 1 15.5-6.2L21 8" />
                    <path d="M21 3v5h-5" />
                    <path d="M21 12a9 9 0 0 1-15.5 6.2L3 16" />
                    <path d="M3 21v-5h5" />
                  </svg>
                </div>
                <div className={styles.statLabel}>累计回收</div>
                <div className={styles.statValue}>
                  {totalRecycle.toLocaleString()}
                </div>
              </button>
              <div className={styles.stat}>
                <div className="stat-icon purple">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 19V9" />
                    <path d="M10 19V5" />
                    <path d="M16 19v-8" />
                    <path d="M22 19V3" />
                  </svg>
                </div>
                <div className={styles.statLabel}>最高峰值</div>
                <div className={styles.statValue}>
                  {peak.activate.toLocaleString()}
                  <span className={styles.statHint}>{peakLabel}</span>
                </div>
              </div>
              <div className={styles.stat}>
                <div className="stat-icon orange">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 3v18h18" />
                    <path d="m7 14 3-3 3 3 4-5" />
                  </svg>
                </div>
                <div className={styles.statLabel}>日均激活</div>
                <div className={styles.statValue}>{fmtAvg}</div>
              </div>
            </div>
            <BarChart points={points} isHourly={isHourly} />
          </>
        ) : (
          <div className="error">暂无数据</div>
        )}
      </div>
      <ProxyRecordsModal
        nested
        open={logsOpen}
        onOpenChange={setLogsOpen}
        proxyId={proxyId}
        proxyName={proxyName}
        period={period}
        kind={recordsKind}
      />
    </Modal>
  );
};